import styled from '@emotion/native';
import { GroupSecurity } from '@quromedical/fhir-common';
import { Flex, Row } from 'components/base';
import { Icon, type IconName, Text, Visible } from 'design-system/base';
import { Logo, Profile } from 'design-system/navigation';
import { BASE_SIZE, BORDER_WIDTH, MARGIN, PADDING } from 'design-system/theme';
import { router, usePathname } from 'expo-router';
import { useUserSession } from 'hooks';
import { useIsDeviceSmallerThan } from 'hooks/useResponsive';
import * as React from 'react';
import { Platform } from 'react-native';
import { strings } from 'strings';

import { getPathComparator } from './helpers';

export const Topbar: React.FC<TabNavigationConfig> = ({ actions = [] }) => {
  const currentRouteName = usePathname();
  const isMobile = Platform.OS !== 'web';
  const isSmall = useIsDeviceSmallerThan('mdl');

  const session = useUserSession();
  const isRoute = React.useMemo(
    () => getPathComparator(currentRouteName),
    [currentRouteName],
  );
  const isAdmin = session.hasAny([GroupSecurity.Admin]);
  const user = session.user;

  const patientReferralRedirect = 'https://quromedical.co.za/patient-form/';

  const openURL = async () => {
    router.push(patientReferralRedirect);
  };

  const navigateToProfile = React.useCallback(() => {
    router.push('/profile');
  }, []);

  return (
    <TopBarContainer unsetZIndex>
      <Row unsetZIndex>
        <Logo />
        <Tab
          isActive={currentRouteName === '/'}
          text="Home"
          icon="home"
          onPress={() => router.push('/')}
        />

        {/* Hide dashboard tab on mobile devices */}
        <Visible if={!isMobile && !isSmall}>
          <Tab
            isActive={isRoute('dashboard')}
            text="Dashboard"
            icon="dashboard"
            onPress={() => router.push('dashboard')}
          />
        </Visible>

        <Tab
          isActive={isRoute('patient')}
          text="Patients"
          icon="people-outline"
          onPress={() => router.push('patient')}
        />

        <Visible if={!isAdmin}>
          <Tab
            isActive={currentRouteName === 'patient-form'}
            text={strings.ReferPatient}
            icon="person-add"
            onPress={() => openURL()}
          />
        </Visible>

        <Visible if={isAdmin}>
          <Tab
            isActive={isRoute('admin')}
            text="Admin"
            icon="settings"
            onPress={() => router.push('admin')}
          />
        </Visible>

        {actions.map((action) => (
          <Visible key={action.title} if={!action.isHidden}>
            <Tab
              onPress={action.onPress}
              icon={action.icon}
              text={action.title}
              isActive={false}
            />
          </Visible>
        ))}
      </Row>

      <Row unsetZIndex>
        <Profile
          name={user?.email}
          onProfilePress={navigateToProfile}
          wards={session.wards}
          ward={session.ward}
          onWardSelect={session.selectWard}
        />
      </Row>
    </TopBarContainer>
  );
};

const Pressable = styled.Pressable<PressableProps>(({ theme, isActive }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
  height: BASE_SIZE[48],
  marginLeft: MARGIN['2xs'],
  paddingHorizontal: PADDING.xs,
  borderStyle: 'solid',
  borderColor: isActive ? theme.color.primary : 'transparent',
  borderWidth: 0,
  borderBottomWidth: BORDER_WIDTH[4],
}));

const IconWrapper = styled(Flex)({
  marginRight: MARGIN['2xs'],
});

export const Tab: React.FC<TabProps> = ({ isActive, onPress, text, icon }) => (
  <Pressable onPress={onPress} isActive={isActive}>
    <IconWrapper isVisible={!!icon}>
      <Icon name={icon as IconName} size={BASE_SIZE[16]} color="text-default" />
    </IconWrapper>
    <Text variant="button" selectable={false}>
      {text}
    </Text>
  </Pressable>
);

const TopBarContainer = styled(Row)(({ theme }) => ({
  backgroundColor: theme.color['base-grey'],
  justifyContent: 'space-between',
  paddingHorizontal: PADDING.m,
  height: BASE_SIZE[48],
  maxWidth: '100%',
}));

interface TabAction {
  icon: IconName;
  title: string;
  isHidden?: boolean;
  onPress: () => void;
}
interface TabProps {
  text: string;
  isActive: boolean;
  icon?: IconName;
  onPress: () => void;
}

interface PressableProps {
  isActive: boolean;
}

// Props accepted by the view
type TabNavigationConfig = {
  name?: string;
  initials?: string;
  actions?: TabAction[];
};
